/**
 * Module dependencies.
 */

import { Container } from 'components/atoms/container/container';
import {
  FlagIcon,
  LanguageSelector,
  Logo,
  LogoWrapper,
  NavbarContent,
  NavbarWrapper,
} from './navbar.style';

import { JSX, useCallback, useEffect, useMemo, useRef } from 'react';
import { Language } from 'types/language';
import { Link } from 'components/atoms/link/link';
import { NavbarMenu } from '../navbar-menu/navbar-menu';
import { getAsset } from 'utils/resolve-admin';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import SvgLogoWhiteLetter from 'assets/logo_white_letter.svg';
import SvgLogoWhiteLogo from 'assets/logo_white_logo.svg';

/**
 * `Props` type.
 */

type Props = {
  languages: Language[];
};

/**
 * Export `Navbar` component.
 */

export function Navbar({ languages }: Props): JSX.Element {
  const textRef = useRef<HTMLDivElement | null>(null);
  const { i18n } = useTranslation();
  const logoRef = useRef<any>(null);
  const languagesRef = useRef<any>(null);
  const menuRef = useRef<any>(null);
  const langs = useMemo(
    () =>
      languages.map((item) => ({ ...item, language: item.code.split('-')[0] })),
    [languages],
  );

  const updatePosition = useCallback(() => {
    if (textRef.current) {
      const rect = textRef.current.getBoundingClientRect();

      if (logoRef.current?.style) {
        logoRef.current.style.left = `${rect.left}px`;
        logoRef.current.style.top = `${rect.top}px`;
        logoRef.current.style.width = `${rect.width}px`;
        logoRef.current.style.height = `${rect.height}px`;
        logoRef.current.style.opacity = 1;
        logoRef.current.style.zIndex = 1000;
      }
    }

    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const langsBox = langs.length * 40;
      const isMobile = rect.width <= 160;

      if (languagesRef.current?.style) {
        const left = rect.right - langsBox - (isMobile ? 55 : 0);

        languagesRef.current.style.left = `${left}px`;
        languagesRef.current.style.width = `${langsBox}px`;
        languagesRef.current.style.top = `${rect.top + rect.height / 2 - 6 - (isMobile ? 1.5 : 0)}px`;

        menuRef.current.style.paddingRight = isMobile ? 0 : `${langsBox}px`;
      }
    }
  }, [langs.length]);

  const router = useRouter();
  const { asPath, locale, pathname, query } = router;
  const changeLanguage = useCallback(
    (newLocale: string) => {
      if (locale === newLocale) return;

      i18n.changeLanguage(newLocale);
      router.push({ pathname, query }, asPath, { locale: newLocale });
    },
    [asPath, i18n, locale, pathname, query, router],
  );

  useEffect(() => {
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    const resizeObserver = new ResizeObserver(updatePosition);

    if (logoRef.current) {
      resizeObserver.observe(logoRef.current);
    }

    updatePosition();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      resizeObserver.disconnect();
    };
  }, [updatePosition]);

  return (
    <>
      <Logo
        as={SvgLogoWhiteLogo}
        ref={logoRef}
        style={{
          opacity: 0,
          position: 'fixed',
        }}
      />

      <NavbarWrapper>
        <Container>
          <NavbarContent>
            <LogoWrapper>
              <Link href={'/'} passHref>
                <Logo
                  as={SvgLogoWhiteLetter}
                  ref={textRef}
                  style={{ position: 'absolute' }}
                />
              </Link>
            </LogoWrapper>

            <NavbarMenu languages={languages} ref={menuRef} />
          </NavbarContent>
        </Container>
      </NavbarWrapper>

      <LanguageSelector ref={languagesRef}>
        {langs.map((item) => (
          <FlagIcon
            alt={item.name}
            key={item.code}
            onClick={() => changeLanguage(item.language)}
            selected={i18n.language === item.language}
            src={getAsset(item.icon.filename_disk, { height: 15 })}
          />
        ))}
      </LanguageSelector>
    </>
  );
}
